import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetActionState,
  setErrorState,
  setInitializedState,
  setLoadingState,
  setShowMessageState,
  setSuccessState,
} from 'src/redux/status/slice';
import type { ActionType } from 'src/redux/status/types';
import type { RootState } from 'src/redux/store';

const useStatus = (actionType: ActionType) => {
  const dispatch = useDispatch();

  // Selector to get the state for the specific action type
  const actionState = useSelector((state: RootState) => state.status[actionType]);

  // Dispatch actions for loading, canceled, and error states
  const setLoading = useCallback(
    (loading?: boolean, message?: string) => {
      dispatch(setLoadingState({ actionType, loading: loading ?? true, message: message || '' }));
    },
    [dispatch, actionType],
  );

  const setSuccess = useCallback(
    (message?: string) => {
      dispatch(setSuccessState({ actionType, message: message || '' }));
      dispatch(setShowMessageState({ actionType, show: true }));
    },
    [dispatch, actionType],
  );

  const setError = useCallback(
    (message?: string) => {
      dispatch(setErrorState({ actionType, message: message || '' }));
      dispatch(setShowMessageState({ actionType, show: true }));
    },
    [dispatch, actionType],
  );

  const setShowMessage = useCallback(
    (show: boolean) => {
      dispatch(setShowMessageState({ actionType, show }));
    },
    [dispatch, actionType],
  );

  const resetState = useCallback(() => {
    dispatch(resetActionState({ actionType }));
  }, [dispatch, actionType]);

  const setInitialize = useCallback(() => {
    dispatch(setInitializedState({ actionType, initialized: true }));
  }, [dispatch, actionType]);

  return {
    loading: actionState?.loading || false,
    loadingMessage: actionState?.loadingMessage || '',
    success: actionState?.success || false,
    successMessage: actionState?.successMessage || '',
    errorMessage: actionState?.errorMessage || '',
    showMessage: actionState?.showMessage || false,
    initialized: actionState?.initialized || false,
    setLoading,
    setSuccess,
    setError,
    resetState,
    setShowMessage,
    setInitialize,
  };
};

export default useStatus;
